import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';

const REASONS = [
  ['CHANGE_OF_MIND', '단순변심'],
  ['SIZE_OR_OPTION', '사이즈/옵션 변경'],
  ['DEFECTIVE',      '상품 불량'],
  ['WRONG_ITEM',     '오배송'],
  ['DAMAGED',        '파손'],
  ['MISSING_ITEM',   '상품 누락'],
  ['DELIVERY_ISSUE', '배송 문제'],
  ['OTHER',          '기타'],
];

const PICKUP = [
  ['CARRIER_PICKUP', '택배사 회수 요청'],
  ['CUSTOMER_SEND',  '고객 직접 발송'],
  ['SELLER_DIRECT',  '판매자 직접 회수'],
  ['NO_PICKUP',      '회수 없음'],
];

export default function ConsumerClaimRequest() {
  const { orderId, type: routeType } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [selected, setSelected] = useState({}); // item_id: {qty, reason}
  const [claimType, setClaimType] = useState((routeType || 'return').toUpperCase());
  const [reasonCode, setReasonCode] = useState('CHANGE_OF_MIND');
  const [reasonText, setReasonText] = useState('');
  const [pickup, setPickup] = useState('CUSTOMER_SEND');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/api/orders/${orderId}`);
        setOrder(r.data);
      } catch { toast.error('주문 정보를 불러오지 못했습니다.'); }
    })();
  }, [orderId]);

  const items = useMemo(() => order?.items || order?.seller_orders?.flatMap(s => s.items) || [], [order]);

  const toggle = (it) => {
    setSelected(s => {
      const key = it.seller_order_item_id || it.product_id;
      if (s[key]) { const n = { ...s }; delete n[key]; return n; }
      return { ...s, [key]: { qty: 1, product_id: it.product_id,
        seller_order_item_id: it.seller_order_item_id, max: it.quantity || 1 } };
    });
  };

  const changeQty = (key, qty) => {
    setSelected(s => ({ ...s, [key]: { ...s[key],
      qty: Math.max(1, Math.min(s[key].max, Number(qty) || 1)) } }));
  };

  const submit = async () => {
    if (Object.keys(selected).length === 0) return toast.error('상품을 선택하세요.');
    setBusy(true);
    try {
      const items = Object.values(selected).map(v => ({
        seller_order_item_id: v.seller_order_item_id,
        product_id: v.product_id, quantity: v.qty,
      }));
      const payload = {
        type: claimType.toLowerCase(),
        reason_code: reasonCode, reason_text: reasonText,
        items, pickup_method: pickup,
      };
      await api.post(`/api/orders/${orderId}/rma`, payload);
      toast.success('신청이 접수되었습니다.');
      nav('/my/rma');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '신청 실패');
    } finally { setBusy(false); }
  };

  const pickupNotice = pickup === 'CARRIER_PICKUP'
    ? '택배사 자동 회수 연동은 준비 중입니다. 판매자 확인 후 회수 접수가 진행됩니다.'
    : null;

  return (
    <div className="max-w-2xl mx-auto p-4" data-testid="consumer-claim-page">
      <h1 className="text-lg font-semibold mb-4" data-testid="claim-title">
        {claimType === 'RETURN' ? '반품 신청' : '교환 신청'}
      </h1>
      <div className="flex gap-2 mb-4" data-testid="claim-type-tabs">
        <button data-testid="claim-type-return"
          className={`px-3 py-1 text-sm rounded ${claimType === 'RETURN' ? 'bg-neutral-900 text-white' : 'bg-neutral-100'}`}
          onClick={() => setClaimType('RETURN')}>반품</button>
        <button data-testid="claim-type-exchange"
          className={`px-3 py-1 text-sm rounded ${claimType === 'EXCHANGE' ? 'bg-neutral-900 text-white' : 'bg-neutral-100'}`}
          onClick={() => setClaimType('EXCHANGE')}>교환</button>
      </div>

      <div className="border rounded divide-y mb-4" data-testid="claim-items-list">
        {items.length === 0 && <div className="p-4 text-sm text-neutral-500">주문 상품이 없습니다.</div>}
        {items.map((it) => {
          const key = it.seller_order_item_id || it.product_id;
          const sel = selected[key];
          return (
            <div key={key} className="p-3 flex items-center justify-between text-sm"
                  data-testid={`claim-item-${key}`}>
              <label className="flex items-center gap-2 flex-1 min-w-0">
                <input type="checkbox" checked={!!sel} onChange={() => toggle(it)}
                        data-testid={`claim-item-checkbox-${key}`} />
                <span className="truncate">{it.name_snapshot || it.product_name || it.product_id}</span>
              </label>
              <div className="text-neutral-500 mr-3">주문 {it.quantity}개</div>
              {sel && (
                <input type="number" min={1} max={sel.max} value={sel.qty}
                        onChange={e => changeQty(key, e.target.value)}
                        className="w-16 border rounded px-2 py-1"
                        data-testid={`claim-item-qty-${key}`} />
              )}
            </div>
          );
        })}
      </div>

      <div className="mb-3">
        <label className="block text-xs mb-1">사유</label>
        <select value={reasonCode} onChange={e => setReasonCode(e.target.value)}
                 className="border rounded px-2 py-1 w-full text-sm"
                 data-testid="claim-reason">
          {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      <div className="mb-3">
        <label className="block text-xs mb-1">상세 사유 (선택)</label>
        <textarea rows={3} className="border rounded px-2 py-1 w-full text-sm"
                   value={reasonText} onChange={e => setReasonText(e.target.value)}
                   data-testid="claim-reason-text" />
      </div>

      <div className="mb-3">
        <label className="block text-xs mb-1">회수 방법</label>
        <select value={pickup} onChange={e => setPickup(e.target.value)}
                 className="border rounded px-2 py-1 w-full text-sm"
                 data-testid="claim-pickup-method">
          {PICKUP.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {pickupNotice && (
          <div className="text-xs text-amber-600 mt-1" data-testid="claim-pickup-notice">
            🟡 {pickupNotice}
          </div>
        )}
      </div>

      <button disabled={busy} onClick={submit}
               className="w-full py-2 bg-emerald-600 text-white text-sm rounded disabled:opacity-50"
               data-testid="claim-submit">
        {busy ? '신청 중…' : '신청하기'}
      </button>
    </div>
  );
}
